import React, { useState } from "react";
import FileExplorer from "./FileExplorer";
import { useWorkspaceStore } from "../../store/workspace";
import {
  ChevronsLeft,
  ChevronsRight,
  X,
  MessageSquare,
  FilePlus,
  Settings as SettingsIcon,
  LogOut,
} from "lucide-react";
import Editor from "../Editor";
import EditorFooter from "../Editor/Footer";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import Settings from "../Settings";
import "./styles.css";

const Workspace: React.FC = () => {
  const {
    createDocument,
    documents,
    activeDocumentId,
    setActiveDocument,
    openDocuments,
    closeDocument,
  } = useWorkspaceStore();

  const [leftSidebarVisible, setLeftSidebarVisible] = useState(true);
  const [rightSidebarVisible, setRightSidebarVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const activeDocument = activeDocumentId ? documents[activeDocumentId] : null;

  const handleCreateNewDocument = () => {
    createDocument("新文档");
  };

  // 关闭文档标签的处理函数
  const handleCloseTab = (docId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // 阻止事件冒泡，避免触发标签选择
    closeDocument(docId);
  };

  return (
    <div className="main-layout h-full">
      {/* 顶部工具栏 */}
      <div className="main-toolbar">
        {/* 左侧控制区域 */}
        <div className="toolbar-left">
          <div className="toolbar-spacer"></div>
          <button
            className="toolbar-button"
            onClick={() => setLeftSidebarVisible(!leftSidebarVisible)}
            title={leftSidebarVisible ? "隐藏侧边栏" : "显示侧边栏"}
          >
            {leftSidebarVisible ? (
              <ChevronsLeft size={16} />
            ) : (
              <ChevronsRight size={16} />
            )}
          </button>
        </div>

        {/* 右侧控制区域 */}
        <div className="toolbar-right">
          <button
            className="toolbar-button"
            onClick={() => setRightSidebarVisible(!rightSidebarVisible)}
            title={rightSidebarVisible ? "隐藏AI助手" : "显示AI助手"}
          >
            <MessageSquare size={16} />
          </button>

          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button className="user-avatar" aria-label="用户菜单">
                AI
              </button>
            </DropdownMenu.Trigger>

            <DropdownMenu.Portal>
              <DropdownMenu.Content
                className="user-dropdown-content"
                sideOffset={5}
                align="end"
              >
                <DropdownMenu.Item
                  className="user-dropdown-item"
                  onClick={() => setShowSettings(true)}
                >
                  <SettingsIcon className="w-4 h-4" />
                  <span>设置</span>
                </DropdownMenu.Item>

                <DropdownMenu.Separator className="user-dropdown-separator" />

                <DropdownMenu.Item className="user-dropdown-item text-red-500">
                  <LogOut className="w-4 h-4" />
                  <span>退出登录</span>
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </div>
      </div>

      {/* 设置弹窗 */}
      <Settings open={showSettings} onOpenChange={setShowSettings} />

      {/* 主内容区域 */}
      <div className="panels-container h-full">
        {/* 左侧边栏 */}
        {leftSidebarVisible && (
          <div className="sidebar">
            <FileExplorer />
          </div>
        )}

        {/* 主编辑区域 */}
        <div className="main-content">
          {openDocuments.length > 0 && (
            <div className="document-tabs-bar">
              {openDocuments.map((docId) => {
                const doc = documents[docId];
                return (
                  <div
                    key={docId}
                    className={`document-tab flex justify-between ${
                      activeDocumentId === docId ? "active" : ""
                    }`}
                    onClick={() => setActiveDocument(docId)}
                  >
                    <span>{doc.title || "未命名"}</span>
                    <button
                      className="tab-close-button"
                      onClick={(e) => handleCloseTab(docId, e)}
                      title="关闭文档"
                    >
                      <X size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* 编辑器内容 */}
          {activeDocument ? (
            <Editor documentId={activeDocumentId!} />
          ) : (
            <div className="empty-editor-state">
              <div className="flex flex-col items-center justify-center h-full">
                <p className="empty-editor-message">没有打开的文档</p>
                <button
                  className="toolbar-button-with-text"
                  onClick={handleCreateNewDocument}
                >
                  <FilePlus className="mr-2" />
                  创建新文档
                </button>
              </div>
            </div>
          )}
          
          {/* 编辑器底部工具栏 */}
          {activeDocument && <EditorFooter />}
        </div>

        {/* 右侧边栏 - AI助手 */}
        {rightSidebarVisible && (
          <div className="right-sidebar">
            <div className="sidebar-header">
              <h3 className="text-sm font-medium">AI助手</h3>
              <button
                className="toolbar-button"
                onClick={() => setRightSidebarVisible(false)}
                title="隐藏AI助手"
              >
                <X size={16} />
              </button>
            </div>
            <div className="ai-assistant-panel">
              <p className="ai-assistant-message">AI助手功能开发中...</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Workspace;
